import React, { useId } from 'react';
import styled, { keyframes } from 'styled-components';

const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
`;
const blink = keyframes`
  0%, 92%, 100% { transform: scaleY(1); }
  96% { transform: scaleY(0.1); }
`;
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
  60% { transform: translateY(0); }
`;
const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
`;

const Root = styled.div`
  width: 100%;
  height: 100%;
  transform-origin: center bottom;

  svg { width: 100%; height: 100%; overflow: visible; display: block; }

  .body { transform-box: fill-box; transform-origin: center; }
  .eyes { transform-box: fill-box; transform-origin: center; }
  .pupils {
    transition: transform 0.18s ease;
    transform: translate(calc(var(--look-x, 0) * 3px), 0);
  }
  /* Hands stay hidden so the default bee matches the reference; they only
     rise over the eyes for the "cover eyes" (password revealed) state. */
  .hand {
    opacity: 0;
    transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease;
  }
  .mouthHappy, .mouthSad { display: none; }

  &[data-mood='idle'] .body { animation: ${breathe} 3.2s ease-in-out infinite; }
  &[data-mood='idle'] .eyes,
  &[data-mood='looking'] .eyes { animation: ${blink} 4s ease-in-out infinite; }

  &[data-mood='looking'] .pupils { transform: translate(calc(var(--look-x, 0) * 3px), 2.5px); }

  &[data-mood='covering'] { transform: rotate(-5deg); }
  &[data-mood='covering'] .hand { opacity: 1; }
  &[data-mood='covering'] .handLeft { transform: translateY(-13px); }
  &[data-mood='covering'] .handRight { transform: translateY(-13px); }

  &[data-mood='happy'] { animation: ${bounce} 0.6s ease; }
  &[data-mood='happy'] .mouthNeutral { display: none; }
  &[data-mood='happy'] .mouthHappy { display: block; }

  &[data-mood='sad'] { animation: ${shake} 0.5s ease; }
  &[data-mood='sad'] .mouthNeutral { display: none; }
  &[data-mood='sad'] .mouthSad { display: block; }
  &[data-mood='sad'] .pupils { transform: translate(calc(var(--look-x, 0) * 3px), 2px); }

  @media (prefers-reduced-motion: reduce) {
    &, .body, .eyes, .pupils, .hand {
      animation: none !important;
      transition: none !important;
    }
  }
`;

export default function Mascot({ mood = 'idle', lookX = 0 }) {
  const clipId = useId();
  return (
    <Root data-mood={mood} style={{ '--look-x': lookX }} aria-hidden="true">
      <svg viewBox="0 0 120 120" role="presentation">
        {/* wings */}
        <ellipse className="wing" cx="38" cy="52" rx="16" ry="22" fill="#C6E2F2" transform="rotate(-24 38 52)" />
        <ellipse className="wing" cx="82" cy="52" rx="16" ry="22" fill="#C6E2F2" transform="rotate(24 82 52)" />
        <g className="body">
          {/* stinger */}
          <path d="M60 112 L54 101 L66 101 Z" fill="#2B2B2B" />
          {/* lower body */}
          <ellipse cx="60" cy="78" rx="28" ry="26" fill="#FBBF24" />
          {/* bold stripes, clipped to the body */}
          <clipPath id={clipId}><ellipse cx="60" cy="78" rx="28" ry="26" /></clipPath>
          <g clipPath={`url(#${clipId})`}>
            <rect x="30" y="67" width="60" height="10" fill="#2B2B2B" />
            <rect x="30" y="86" width="60" height="12" fill="#2B2B2B" />
          </g>
          {/* head */}
          <circle cx="60" cy="42" r="23" fill="#FBBF24" />
          {/* antennae — thin curved lines, no balls */}
          <path d="M52 22 Q47 9 51 4" stroke="#2B2B2B" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M68 22 Q73 9 69 4" stroke="#2B2B2B" strokeWidth="2" fill="none" strokeLinecap="round" />
          {/* blush cheeks */}
          <ellipse cx="44" cy="48" rx="5.5" ry="3.8" fill="#F6868F" opacity="0.85" />
          <ellipse cx="76" cy="48" rx="5.5" ry="3.8" fill="#F6868F" opacity="0.85" />
          {/* eyes */}
          <g className="eyes">
            <circle cx="51" cy="39" r="8.5" fill="#fff" />
            <circle cx="69" cy="39" r="8.5" fill="#fff" />
            <g className="pupils">
              <circle cx="51" cy="40.5" r="4.6" fill="#2B2B2B" />
              <circle cx="69" cy="40.5" r="4.6" fill="#2B2B2B" />
              <circle cx="52.6" cy="38.5" r="1.5" fill="#fff" />
              <circle cx="70.6" cy="38.5" r="1.5" fill="#fff" />
            </g>
          </g>
          {/* mouth */}
          <path className="mouthNeutral" d="M54 51 Q60 57 66 51" stroke="#2B2B2B" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path className="mouthHappy" d="M53 50 Q60 61 67 50 Z" fill="#2B2B2B" />
          <path className="mouthSad" d="M54 56 Q60 50 66 56" stroke="#2B2B2B" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          {/* hands — hidden by default, cover the eyes on 'covering' */}
          <ellipse className="hand handLeft" cx="51" cy="50" rx="8" ry="6.5" fill="#F6B92E" stroke="#E09E1E" strokeWidth="1" />
          <ellipse className="hand handRight" cx="69" cy="50" rx="8" ry="6.5" fill="#F6B92E" stroke="#E09E1E" strokeWidth="1" />
        </g>
      </svg>
    </Root>
  );
}
